import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { nextTick, ref, type Ref } from 'vue';
import { measureAsyncDuration } from '../../../utils/performanceUtils';
import { CatalogPdfService } from '../services/catalog-pdf.service';
import type { CatalogData } from '../types/catalog-pdf.types';
import { recordCatalogPdfGenerationError, recordCatalogPdfGenerationMetrics } from '../utils/catalog-pdf-observability';

export function useCatalogPdfGeneration(printContainerRef: Ref<HTMLElement | null>) {
  const generatingPdf = ref(false);
  const catalogData = ref<CatalogData | null>(null);

  const userStore = useUserStore();
  const { selectedAccountId } = storeToRefs(useUserStore());
  const catalogPdfService = new CatalogPdfService();

  const generateCatalogPdf = async (season: string) => {
    if (!selectedAccountId.value || !season) return;

    generatingPdf.value = true;

    try {
      const { result: productData, durationMs: fetchDurationMs } = await measureAsyncDuration(() =>
        catalogPdfService.getSeasonCatalogData(selectedAccountId.value, season),
      );

      if (!productData.isValid() || !productData.model) {
        throw new Error('seasonCatalogPdf.error.data_not_found');
      }

      catalogData.value = productData.model;

      // Wait for Vue to render CatalogPdf into the container before printing
      await nextTick();

      const { printCatalogPdf } = await import('../services/catalog-pdf-generate.service');

      if (!printContainerRef.value) {
        throw new Error('seasonCatalogPdf.error.error_while_generation');
      }

      const metrics = await printCatalogPdf(printContainerRef.value, productData.model);

      recordCatalogPdfGenerationMetrics({
        ...metrics,
        fetchDurationMs,
        accountId: selectedAccountId.value,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
        season,
        catalogData: productData.model,
      });
    } catch (err: any) {
      const error = err instanceof Error ? err : new Error(String(err));

      recordCatalogPdfGenerationError({
        error,
        accountId: selectedAccountId.value,
        accountName: userStore.selectedAccount.name,
        legalEntity: userStore.selectedAccount.legalEntity,
        season,
      });

      throw error;
    } finally {
      catalogData.value = null;
      generatingPdf.value = false;
    }
  };

  return {
    generatingPdf,
    catalogData,
    generateCatalogPdf,
  };
}
